import {FormEvent} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {usePost, usePostMutations} from '../api/posts';
import {useTodoMutations} from '../api/todos';
import Button from '../components/Button';
import ErrorBox, {FieldError, isGeneralError} from '../components/feedback/ErrorBox';
import {SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import PhotoStrip from '../components/tell/PhotoStrip';
import PlantField from '../components/tell/PlantField';
import WhereChips from '../components/tell/WhereChips';
import WhenChips from '../components/todo/WhenChips';
import {useTellIt} from '../components/todo/useTellIt';
import {useMe} from '../hooks/useAuth';
import {confirm} from '../hooks/useConfirm';
import {useFormDraft} from '../hooks/useFormDraft';
import {toast} from '../hooks/useToast';
import {kindInfo} from '../lib/kinds';
import {nowLocal, offsetMinutes, todayLocal} from '../lib/when';
import {Post, Repeat} from '../types';

// A to-do, new or existing (DESIGN.md §6, To do form): the words ("What needs doing?"), When as
// presets, Repeat, Plant and Where as on a tell, and photos once it is saved. An existing one
// can be ticked off, reopened or deleted. Unsaved words survive leaving, as every form's do.

interface Values {
    text: string;
    dueLocal: string | null;
    repeat: Repeat | null;
    plants: string[];
    placeId: string | null;
}

const fromTodo = (todo: Post | null): Values => ({
    text: todo?.text ?? '',
    dueLocal: todo?.dueLocal ?? null,
    repeat: todo?.repeat ?? null,
    plants: todo?.plants.map(p => p.label) ?? [],
    placeId: todo?.place?.id ?? null
});

const REPEATS: {value: Repeat | null; label: string}[] = [
    {value: null, label: 'Never'},
    {value: 'weekly', label: 'Every week'},
    {value: 'yearly', label: 'Every year'}
];

function TodoForm({todo}: {todo: Post | null}) {
    const navigate = useNavigate();
    const me = useMe();
    const {create, patch, done, reopen} = useTodoMutations();
    const {softDelete} = usePostMutations();
    const tellIt = useTellIt();
    const today = todayLocal();
    const form = useFormDraft<Values>(todo ? `todo:${todo.id}` : 'todo:new', fromTodo(todo), {
        label: todo ? `Unsaved changes to “${todo.title || 'a to-do'}”` : 'A new to-do',
        route: todo ? `/todo/${todo.id}` : '/todo/new'
    });
    const v = form.values;
    const saving = create.isPending || patch.isPending;
    const error = create.error ?? patch.error;
    const needsDate = v.repeat !== null && v.dueLocal === null;

    const leave = () => navigate('/todo', {replace: true});

    const submit = (event: FormEvent) => {
        event.preventDefault();
        const input = {text: v.text, dueLocal: v.dueLocal, repeat: v.repeat, plants: v.plants, placeId: v.placeId};
        if (todo) {
            patch.mutate({id: todo.id, ...input}, {onSuccess: () => { form.clear(); toast.success('Saved.'); leave(); }});
        } else {
            create.mutate({...input, whenLocal: nowLocal(), whenOffsetMinutes: offsetMinutes()}, {onSuccess: () => { form.clear(); toast.success('On your To do list.'); leave(); }});
        }
    };

    const remove = async () => {
        if (!todo) return;
        const removed = await confirm({
            title: 'Delete this to-do?',
            body: 'It goes to Recently deleted.',
            help: 'You can bring it back from More → Recently deleted for 30 days.',
            confirmLabel: 'Delete',
            destructive: true,
            action: () => softDelete.mutateAsync(todo.id)
        });
        if (removed) {
            form.discard();
            leave();
        }
    };

    return (
        <form onSubmit={submit} className="settle-in flex min-h-dvh flex-col">
            <header className="-mx-4 flex items-center justify-between gap-2 border-b border-line py-3 pl-3 pr-4">
                <Link to="/todo" aria-label="Back" className="flex h-11 w-11 items-center justify-center rounded-pill text-ink no-underline">
                    <Icon name="back" size={22} />
                </Link>
                <span className="font-display text-lg font-bold">{todo ? 'To do' : 'New to-do'}</span>
                <Button type="submit" busy={saving} className="min-h-[44px] rounded-pill px-5 text-[15px] font-bold" disabled={v.text.trim() === '' || needsDate}>
                    Save
                </Button>
            </header>

            <div className="flex flex-1 flex-col gap-5 pb-10 pt-[18px]">
                {form.restored && (
                    <div className="nudge flex items-center gap-3">
                        <span className="flex-1">Picked up your unsaved changes from earlier.</span>
                        <button type="button" className="font-display font-bold underline-offset-2 hover:underline" onClick={form.discard}>
                            Discard changes
                        </button>
                    </div>
                )}

                {todo?.follows && (
                    <Link to={`/tell/${todo.follows.id}`} className="flex items-center gap-3 rounded-[14px] border border-line bg-surface p-2 no-underline">
                        {todo.follows.thumbUrl && <img src={todo.follows.thumbUrl} alt="" className="h-12 w-12 rounded-[10px] object-cover" />}
                        <span className="meta min-w-0 flex-1">
                            Following up your tell
                            <span className="block truncate font-semibold text-ink">{todo.follows.title || kindInfo(todo.follows.kind).label}</span>
                        </span>
                        <Icon name="chevron" size={18} className="text-ink-3" />
                    </Link>
                )}

                {todo && <PhotoStrip postId={todo.id} photos={todo.photos} />}

                <label className="flex flex-col gap-2">
                    <span className="text-xs font-semibold text-ink-2">
                        What needs doing? <span className="font-medium">The first line is the title.</span>
                    </span>
                    <textarea
                        className="min-h-[80px] w-full resize-y bg-transparent font-display text-[21px] font-semibold leading-[1.3] tracking-[-0.01em] outline-none placeholder:font-normal placeholder:text-ink-3"
                        rows={2}
                        maxLength={4000}
                        placeholder="Plant soy and chickpeas…"
                        value={v.text}
                        onChange={e => form.set({text: e.target.value})}
                        autoFocus={!todo}
                    />
                    <FieldError error={error} field="text" />
                </label>

                <WhenChips value={v.dueLocal} onChange={dueLocal => form.set({dueLocal})} today={today} hemisphere={me.data?.hemisphere} />
                <FieldError error={error} field="dueLocal" />

                <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                    <legend className="band-label mb-1 p-0 text-ink-2">Repeat</legend>
                    <div className="flex flex-wrap gap-2">
                        {REPEATS.map(r => (
                            <button key={r.label} type="button" className="chip" aria-pressed={v.repeat === r.value} onClick={() => form.set({repeat: r.value})}>
                                {r.label}
                            </button>
                        ))}
                    </div>
                    {needsDate && <p className="meta m-0">Pick a date for it to repeat from.</p>}
                </fieldset>

                <PlantField values={v.plants} onChange={plants => form.set({plants})} />
                <WhereChips value={v.placeId} onChange={placeId => form.set({placeId})} />

                {isGeneralError(error) && <ErrorBox error={error} />}

                {todo && (
                    <div className="mt-4 flex flex-col gap-2">
                        {todo.doneAt === null ? (
                            <Button variant="secondary" busy={done.isPending} onClick={() => done.mutateAsync(todo.id).then(post => { form.discard(); tellIt(todo, post.doneAt === null); leave(); })}>
                                <Icon name="check" size={20} /> Done
                            </Button>
                        ) : (
                            <Button variant="secondary" busy={reopen.isPending} onClick={() => reopen.mutateAsync(todo.id).then(() => toast.success('Back on the list.'))}>
                                Not done after all
                            </Button>
                        )}
                        <Button variant="secondary" danger onClick={remove}>
                            Delete this to-do
                        </Button>
                    </div>
                )}
            </div>
        </form>
    );
}

export default function TodoFormPage() {
    const {id} = useParams<{id: string}>();
    const post = usePost(id);
    if (!id) return <TodoForm todo={null} />;
    if (post.isError) return <ErrorBox error={post.error} onRetry={() => void post.refetch()} className="mt-16" />;
    if (!post.data) return <SkeletonList rows={5} />;
    return <TodoForm key={post.data.id} todo={post.data} />;
}
